import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { EmptyStateMessage, Tile } from '../../detail/tabs/Tile';
import { ConsultationContentType } from '../../detail/tabs/contentTypes';
import { ConsultationStatusEnum } from '../../enums/consultationStatusEnum';
import { ConsultationService, sharedApiClient } from '../../fetcher';
import { useNotifications, CustomDropdown, getAssigneeName as resolveAssigneeName, createAssigneeOptions, getAssigneeIcon, outOfOfficeForAssignee } from '@medprompt/shared';
import { TaskTypeEnum } from '../../enums/taskTypeEnum';
import { UrgencyEnum, getUrgency } from '../../enums/urgencyEnum';
import AddOutcomeTaskForm from '../../detail/AddOutcomeTaskForm';

const consultationShouldShowContent = (field, status) => {
  return field !== undefined || status === ConsultationStatusEnum.READY.value;
};

const OutcomeTasksTab = ({ 
  consultation, 
  isEditable, 
  isRegenerationEnabled, 
  handleRegenerationClick, 
  regenerateService,
  onConsultationUpdate,
  activeUsers = [],
  teams = [],
  onTranscriptSearch
}) => {
  const consultationManager = useMemo(() => new ConsultationService(sharedApiClient), []);
  const { addNotification } = useNotifications();
  
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editableTaskText, setEditableTaskText] = useState('');
  const [taskDropdownOpen, setTaskDropdownOpen] = useState({});
  const [taskTypeDropdownOpen, setTaskTypeDropdownOpen] = useState({});
  const [urgencyDropdownOpen, setUrgencyDropdownOpen] = useState({});
  const [showAddTaskForm, setShowAddTaskForm] = useState(false);
  
  const taskTextareaRefs = useRef({});

  const status = consultation?.status;
  const consultationId = consultation?.consultationId;
  const outcomeTasks = consultation?.outcomeTasks;

  // Focus textarea when editing starts
  useEffect(() => {
    if (editingTaskId && taskTextareaRefs.current[editingTaskId]) {
      taskTextareaRefs.current[editingTaskId].focus();
    }
  }, [editingTaskId]);

  const handleTaskClick = useCallback((taskId, currentText) => {
    if (!isEditable) return;
    setEditingTaskId(taskId);
    setEditableTaskText(currentText || '');
  }, [isEditable]);

  const handleTaskBlur = useCallback((taskId, originalText) => {
    if (!consultationId) return;
    
    setEditingTaskId(null);
    
    // Auto-save if there are changes
    if (editableTaskText !== (originalText || '') && editableTaskText.trim()) {
      const updatedTasks = outcomeTasks?.map(task =>
        task.taskId === taskId ? { ...task, text: editableTaskText } : task
      );
      
      const updatedConsultation = { ...consultation, outcomeTasks: updatedTasks };
      
      if (onConsultationUpdate) {
        onConsultationUpdate(updatedConsultation);
      }
      
      consultationManager.updateConsultationTask(consultationId, { taskId, text: editableTaskText })
        .catch(error => {
          console.error('Error updating task text:', error);
          addNotification(`task-save-error-${taskId}`, 'Failed to save task changes', { isError: true });
        });
    }
    setEditableTaskText('');
  }, [consultationId, editableTaskText, outcomeTasks, consultation, onConsultationUpdate, consultationManager, addNotification]);

  const handleTaskKeyDown = useCallback((e, taskId, originalText) => {
    if (e.key === 'Escape') {
      setEditableTaskText(originalText || '');
      setEditingTaskId(null);
    }
  }, []);

  const handleTaskTypeChange = useCallback((taskId, newTaskType) => {
    if (!isEditable || !consultationId) return;
    
    const updatedTasks = outcomeTasks?.map(task =>
      task.taskId === taskId ? { ...task, taskType: newTaskType } : task
    );
    
    const updatedConsultation = { ...consultation, outcomeTasks: updatedTasks };
    
    if (onConsultationUpdate) {
      onConsultationUpdate(updatedConsultation);
    }
    
    consultationManager.updateConsultationTask(consultationId, { taskId, taskType: newTaskType })
      .catch(error => {
        console.error('Error updating task type:', error);
        addNotification(`task-type-error-${taskId}`, 'Failed to update task type', { isError: true });
      });
    
    setTaskTypeDropdownOpen((prev) => ({
      ...prev,
      [taskId]: false,
    }));
  }, [isEditable, consultationId, outcomeTasks, consultation, onConsultationUpdate, consultationManager, addNotification]);

  const handleTaskAssigneeChange = useCallback((taskId, userId) => {
    if (!isEditable || !consultationId) return;
    
    const updatedTasks = outcomeTasks?.map(task =>
      task.taskId === taskId ? { ...task, assigneeId: userId } : task
    );
    
    const updatedConsultation = { ...consultation, outcomeTasks: updatedTasks };
    
    if (onConsultationUpdate) {
      onConsultationUpdate(updatedConsultation);
    }
    
    // Send '*' to backend when unassigning (userId is null)
    const userIdToSend = userId === null ? '*' : userId;
    consultationManager.updateConsultationTask(consultationId, { taskId, assigneeId: userIdToSend })
      .catch(error => {
        console.error('Error updating task assignee:', error);
        addNotification(`task-assignee-error-${taskId}`, 'Failed to update task assignee', { isError: true });
      });
    
    setTaskDropdownOpen((prev) => ({
      ...prev,
      [taskId]: false,
    }));
  }, [isEditable, consultationId, outcomeTasks, consultation, onConsultationUpdate, consultationManager, addNotification]);

  const handleTaskUrgencyChange = useCallback((taskId, isUrgent) => {
    if (!isEditable || !consultationId) return;
    
    const updatedTasks = outcomeTasks?.map(task =>
      task.taskId === taskId ? { ...task, isUrgent: isUrgent } : task
    );
    
    const updatedConsultation = { ...consultation, outcomeTasks: updatedTasks };
    
    if (onConsultationUpdate) {
      onConsultationUpdate(updatedConsultation);
    }
    
    const urgencyUpdate = isUrgent ? { isUrgent: true } : { isNotUrgent: true };
    consultationManager.updateConsultationTask(consultationId, { taskId, ...urgencyUpdate })
      .catch(error => {
        console.error('Error updating task urgency:', error);
        addNotification(`task-urgency-error-${taskId}`, 'Failed to update task urgency', { isError: true });
      });
    
    setUrgencyDropdownOpen((prev) => ({
      ...prev,
      [taskId]: false,
    }));
  }, [isEditable, consultationId, outcomeTasks, consultation, onConsultationUpdate, consultationManager, addNotification]);

  // Handler for individual task selection change
  const handleTaskSelectionChange = useCallback((taskId, isChecked) => {
    if (!isEditable || !consultationId) return;
    
    // Update local state
    const updatedTasks = outcomeTasks?.map(task =>
      task.taskId === taskId ? { ...task, isSelected: isChecked } : task
    );
    
    const updatedConsultation = { ...consultation, outcomeTasks: updatedTasks };
    
    if (onConsultationUpdate) {
      onConsultationUpdate(updatedConsultation);
    }
    
    const isSelected = isChecked ? true : null;
    const isDeselected = !isChecked ? true : null;
    consultationManager.updateConsultationTask(consultationId, { taskId, isSelected, isDeselected })
      .catch(error => {
        console.error('Error updating task selection:', error);
        addNotification(`task-select-error-${taskId}`, 'Failed to update task selection', { isError: true });
      });
  }, [isEditable, consultationId, outcomeTasks, consultation, onConsultationUpdate, consultationManager, addNotification]);

  // Toggle functions for dropdowns
  const toggleTaskTypeDropdown = useCallback((taskId) => {
    setTaskTypeDropdownOpen((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  }, []);
  
  const toggleTaskDropdown = useCallback((taskId) => {
    setTaskDropdownOpen((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  }, []);

  const toggleUrgencyDropdown = useCallback((taskId) => {
    setUrgencyDropdownOpen((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  }, []);

  // Get assignee name
  const getAssigneeName = useCallback((userId) => {
    return resolveAssigneeName(userId, activeUsers, teams);
  }, [activeUsers, teams]);

  // Handler for selecting all outcome tasks
  const handleSelectAllTasks = useCallback((e) => {
    if (!isEditable || !consultationId) return;
    
    const checked = e.target.checked;
    
    // Update local state for all tasks
    const updatedTasks = outcomeTasks?.map(task => {
      return { ...task, isSelected: checked };
    });
    
    const updatedConsultation = { ...consultation, outcomeTasks: updatedTasks };
    
    if (onConsultationUpdate) {
      onConsultationUpdate(updatedConsultation);
    }
    
    // Send selection state to backend for each task
    outcomeTasks?.forEach((task) => {
      const isSelected = checked ? true : null;
      const isDeselected = !checked ? true : null;
      consultationManager.updateConsultationTask(consultationId, { taskId: task.taskId, isSelected, isDeselected })
        .catch(error => {
          console.error(`Error updating task selection for ${task.taskId}:`, error);
        });
    });
  }, [isEditable, consultationId, outcomeTasks, consultation, onConsultationUpdate, consultationManager]);

  const handleAddTask = useCallback((newTask) => {
    if (!consultationId) return;
    consultationManager.addOutcomeTasks(consultationId, [newTask])
      .then(() => {
        setShowAddTaskForm(false);
      })
      .catch(error => {
        console.error('Error adding task:', error);
        addNotification(`task-add-error-${consultationId}`, 'Failed to add task', { isError: true });
      });
  }, [consultationId, consultationManager, addNotification]);

  const renderTaskItem = (task) => {
    // Create task type options from the enum
    const taskTypeOptions = Object.keys(TaskTypeEnum)
      .map(typeKey => ({ 
        id: typeKey, 
        description: TaskTypeEnum[typeKey].description,
        icon: TaskTypeEnum[typeKey].icon
      }));

    // Create urgency options
    const urgencyOptions = [
      { 
        id: true, 
        description: UrgencyEnum.URGENT.description,
        icon: UrgencyEnum.URGENT.icon
      },
      { 
        id: false, 
        description: UrgencyEnum.NORMAL.description,
        icon: UrgencyEnum.NORMAL.icon
      }
    ];

    // Create assignee options with teams and users
    const assigneeOptions = createAssigneeOptions(activeUsers, teams);
    const selectedIcon = getAssigneeIcon(task.assigneeId, activeUsers, teams);
    
    // Get current urgency
    const currentUrgency = getUrgency(task.isUrgent || false);

    return (
      <div key={task.taskId} className="document-ai-item" id="ai-outcome-task-item">
        <div className="outcome-details">
          <div className="task-text-container">
            {editingTaskId === task.taskId ? (
              <textarea
                ref={(el) => taskTextareaRefs.current[task.taskId] = el}
                value={editableTaskText}
                onChange={(e) => setEditableTaskText(e.target.value)}
                onBlur={() => handleTaskBlur(task.taskId, task.text)}
                onKeyDown={(e) => handleTaskKeyDown(e, task.taskId, task.text)}
                className="task-edit-textarea"
                rows="3"
              />
            ) : (
              <div 
                className={`task-text ${isEditable ? 'task-text-editable' : ''}`}
                onClick={() => handleTaskClick(task.taskId, task.text)}
                onMouseMove={(e) => {
                  const tooltip = e.currentTarget.querySelector('.edit-tooltip');
                  if (tooltip) {
                    tooltip.style.left = `${e.clientX + 10}px`;
                    tooltip.style.top = `${e.clientY + 10}px`;
                  }
                }}
              >
                {task.text || <em style={{ color: '#999' }}>No task description</em>}
                {isEditable && <div className="edit-tooltip">Click to edit task</div>}
              </div>
            )}
            {task.quote && (
              <div className="code-quote">
                <span
                  className={`code-quote-text${onTranscriptSearch ? ' clickable' : ''}`}
                  onClick={() => onTranscriptSearch && onTranscriptSearch(task.quote)}
                >
                  "{task.quote}"
                </span>
              </div>
            )}
          </div>

          <div className="custom-select-container-items">
            <div className="custom-select-container">
              <CustomDropdown
                selected={currentUrgency.description}
                selectedIcon={currentUrgency.icon}
                options={urgencyOptions}
                isOpen={urgencyDropdownOpen[task.taskId]}
                toggleDropdown={() => toggleUrgencyDropdown(task.taskId)}
                onSelect={(option) => handleTaskUrgencyChange(task.taskId, option.id)}
                disabled={!isEditable}
                selectedStyle={{ 
                  backgroundColor: currentUrgency.color,
                  color: '#000',
                  fontWeight: 'bold',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
              />
            </div>
            <div className="custom-select-container">
              <CustomDropdown
                selected={TaskTypeEnum[task.taskType]?.description || 'Select Task Type'}
                selectedIcon={TaskTypeEnum[task.taskType]?.icon}
                options={taskTypeOptions}
                isOpen={taskTypeDropdownOpen[task.taskId]}
                toggleDropdown={() => toggleTaskTypeDropdown(task.taskId)}
                onSelect={(option) => handleTaskTypeChange(task.taskId, option.id)}
                disabled={!isEditable}
              />
            </div>
            {task.taskType === 'PATIENT_NOTE' ? (
              <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px' }}>
                <p style={{ fontSize: '0.9em', color: '#666', fontStyle: 'italic', margin: 0 }}>
                  Patient message tasks are excluded when filing away
                </p>
              </div>
            ) : (
              <div className="custom-select-container">
                <CustomDropdown
                  searchable
                  searchPlaceholder="Search assignees..."
                  selected={getAssigneeName(task.assigneeId)}
                  selectedIcon={selectedIcon}
                  selectedOutOfOffice={outOfOfficeForAssignee(task.assigneeId, activeUsers)}
                  options={assigneeOptions}
                  isOpen={taskDropdownOpen[task.taskId]}
                  toggleDropdown={() => toggleTaskDropdown(task.taskId)}
                  onSelect={(assigneeOption) => handleTaskAssigneeChange(task.taskId, assigneeOption.userId)}
                  keyExtractor={(option) => option.id}
                  disabled={!isEditable}
                />
              </div>
            )}
          </div>
        </div>
        <input 
          type="checkbox" 
          className="outcome-checkbox" 
          id={`checkbox_${task.taskId}`} 
          checked={task.isSelected || false}
          onChange={(e) => handleTaskSelectionChange(task.taskId, e.target.checked)}
          disabled={!isEditable}
        />
      </div>
    );
  };

  const renderTasksList = () => {
    const hasTasks = outcomeTasks && outcomeTasks.length > 0;

    return (
      <div className="outcome-tasks-list">
        {showAddTaskForm && (
          <AddOutcomeTaskForm
            activeUsers={activeUsers}
            teams={teams}
            onConfirm={handleAddTask}
            onCancel={() => setShowAddTaskForm(false)}
          />
        )}
        {hasTasks
          ? outcomeTasks.map(renderTaskItem)
          : <EmptyStateMessage message="No outcome tasks found" />
        }
      </div>
    );
  };

  const outcomeTasksTile = (
    <Tile
      doc={consultation}
      entityId={consultationId}
      contentType={ConsultationContentType.OUTCOME_STATUS}
      title="Outcome Tasks"
      condition={consultationShouldShowContent(outcomeTasks, status)}
      isEnabled={isRegenerationEnabled ? isRegenerationEnabled() : false}
      onRegenerateClick={handleRegenerationClick}
      regenerateService={regenerateService}
      showContentOnError
      shouldShowError={() => !(outcomeTasks?.length > 0)}
      headerContent={
        outcomeTasks && outcomeTasks.length > 0 && isEditable && 
          <input 
            type="checkbox" 
            className="code-checkbox outcome-checkbox" 
            onChange={handleSelectAllTasks}
            disabled={!isEditable}
          />
      }
      preContent={
        <div className="tile-header-content">
          <p style={{ fontSize: '0.9em', color: '#666', fontStyle: 'italic' }}>
            Patient message tasks are excluded when filing away.
          </p>
          <p style={{ fontSize: '0.9em', color: '#666', fontStyle: 'italic', marginTop: '-8px' }}>
            They can be used to send messages to the patient instead.
          </p>
        </div>
      }
    >
      {isEditable && !showAddTaskForm && (
        <button
          className="add-measurement-button"
          onClick={() => setShowAddTaskForm(true)}
        >
          + Add Task
        </button>
      )}
      {renderTasksList()}
    </Tile>
  );

  return (
    <div className="document-detail-tiles">
      {outcomeTasksTile}
    </div>
  );
};

export default OutcomeTasksTab;
